import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, Stack, Chip,
  IconButton, TextField, MenuItem, Tooltip, LinearProgress,
  Divider, Paper
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon,
  FolderSpecial as ProjectIcon,
  ArrowBack as ArrowBackIcon, Business as BusinessIcon, Person as PersonIcon,
  DateRange as DateRangeIcon, AttachMoney as MoneyIcon, Flag as FlagIcon
} from '@mui/icons-material';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { projectApi } from '../services/projectApi';
import { PrjProject, ProjectStatus, ProjectPriority } from '../types/project';

const emptyProject = (): Partial<PrjProject> => ({
  title: '',
  client: '',
  status: 'PLANNING',
  priority: 'MEDIUM',
  startDate: new Date().toISOString().split('T')[0],
  endDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  budget: 0,
  spent: 0,
  progress: 0,
  manager: '',
  description: '',
});

const getStatusColor = (status: ProjectStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case 'PLANNING': return 'info';
    case 'IN_PROGRESS': return 'primary';
    case 'ON_HOLD': return 'warning';
    case 'COMPLETED': return 'success';
    case 'CANCELLED': return 'error';
    default: return 'default';
  }
};

const getStatusLabel = (status: ProjectStatus, t: (key: string, fallback?: string) => string): string => {
  switch (status) {
    case 'PLANNING': return t('project.projects.statusPlanning', 'Planification');
    case 'IN_PROGRESS': return t('project.projects.statusInProgress', 'En cours');
    case 'ON_HOLD': return t('project.projects.statusOnHold', 'En pause');
    case 'COMPLETED': return t('project.projects.statusCompleted', 'Terminé');
    case 'CANCELLED': return t('project.projects.statusCancelled', 'Annulé');
    default: return status;
  }
};

const getPriorityColor = (priority: ProjectPriority): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (priority) {
    case 'URGENT': return 'error';
    case 'HIGH': return 'warning';
    case 'MEDIUM': return 'info';
    case 'LOW': return 'default';
    default: return 'default';
  }
};

export const ProjectsPage: React.FC = () => {
  const { t } = useLanguage();

  const [view, setView] = useState<'list' | 'form'>('list');
  const [viewMode, setViewMode] = useDefaultViewMode();
  const [projectsList, setProjectsList] = useState<PrjProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<PrjProject>>(emptyProject());
  const [isEditing, setIsEditing] = useState(false);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const data = await projectApi.getProjects();
      setProjectsList(data);
    } catch (err) {
      console.error('Error loading projects:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const openNew = () => {
    setFormData(emptyProject());
    setIsEditing(false);
    setView('form');
  };

  const openEdit = (project: PrjProject) => {
    setFormData({ ...project });
    setIsEditing(true);
    setView('form');
  };

  const backToList = () => {
    setView('list');
  };

  const handleDelete = async (project: PrjProject) => {
    const projectId = (project.id || project.PRJ_IDF)!;
    const confirmed = await blobticMessage.confirm({
      message: t('project.projects.confirmDelete', 'Êtes-vous sûr de vouloir supprimer ce projet ?'),
      title: t('common.confirm', 'Confirmation')
    });

    if (confirmed) {
      const res = await projectApi.deleteProject(projectId);
      if (res.success) {
        blobticMessage.success(t('project.projects.deleteSuccess', 'Projet supprimé avec succès.'));
        loadProjects();
      } else {
        blobticMessage.error(res.message || t('common.error', 'Erreur lors de la suppression.'));
      }
    }
  };

  const handleValidate = (): string | null => {
    if (!formData.title?.trim()) {
      return t('project.projects.titleRequired', 'Le titre du projet est requis.');
    }
    return null;
  };

  const handleDataChange = () => {
    backToList();
    loadProjects();
  };

  if (view === 'form') {
    const formPayload = {
      ...formData,
      PRJ_IDF: formData.id || formData.PRJ_IDF,
      PRJ_CODE: formData.code || formData.PRJ_CODE,
      PRJ_TITLE: formData.title,
      PRJ_CLIENT: formData.client,
      PRJ_STATUS: formData.status,
      PRJ_PRIORITY: formData.priority,
      PRJ_START_DATE: formData.startDate,
      PRJ_END_DATE: formData.endDate,
      PRJ_BUDGET: Number(formData.budget || 0),
      PRJ_SPENT: Number(formData.spent || 0),
      PRJ_PROGRESS: Number(formData.progress || 0),
      PRJ_MANAGER: formData.manager,
      PRJ_DESCRIPTION: formData.description,
    };

    return (
      <GenericPage<any>
        title={isEditing ? t('project.projects.editTitle', 'Modifier le Projet') : t('project.projects.newTitle', 'Nouveau Projet')}
        icon={<ProjectIcon />}
        footerEnabled={true}
        footerInfo={formData.last_updt ? { lastUpdate: formData.last_updt, userLib: formData.user_lib } : undefined}
        additionalActions={
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={backToList}
          >
            {t('common.backToList', 'Retour à la liste')}
          </Button>
        }
        crudConfig={{ entityKey: 'Blobtic_ProjectManagement,prj_project', primaryKey: 'PRJ_IDF' }}
        data={formPayload}
        onDataChange={handleDataChange}
        onValidate={handleValidate}
        onClear={() => setFormData(emptyProject())}
      >
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {t('project.projects.generalInfo', 'Informations Générales')}
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label={t('project.projects.title', 'Titre du projet *')}
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={t('project.projects.client', 'Client / Bénéficiaire')}
                value={formData.client || ''}
                onChange={(e) => setFormData({ ...formData, client: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label={t('project.projects.status', 'Statut *')}
                value={formData.status || 'PLANNING'}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as ProjectStatus })}
              >
                <MenuItem value="PLANNING">{t('project.projects.statusPlanning', 'Planification')}</MenuItem>
                <MenuItem value="IN_PROGRESS">{t('project.projects.statusInProgress', 'En cours')}</MenuItem>
                <MenuItem value="ON_HOLD">{t('project.projects.statusOnHold', 'En pause')}</MenuItem>
                <MenuItem value="COMPLETED">{t('project.projects.statusCompleted', 'Terminé')}</MenuItem>
                <MenuItem value="CANCELLED">{t('project.projects.statusCancelled', 'Annulé')}</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label={t('project.projects.priority', 'Priorité *')}
                value={formData.priority || 'MEDIUM'}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value as ProjectPriority })}
              >
                <MenuItem value="LOW">{t('project.projects.priorityLow', 'Basse')}</MenuItem>
                <MenuItem value="MEDIUM">{t('project.projects.priorityMedium', 'Moyenne')}</MenuItem>
                <MenuItem value="HIGH">{t('project.projects.priorityHigh', 'Haute')}</MenuItem>
                <MenuItem value="URGENT">{t('project.projects.priorityUrgent', 'Urgente')}</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={t('project.projects.manager', 'Chef de projet / Responsable')}
                value={formData.manager || ''}
                onChange={(e) => setFormData({ ...formData, manager: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label={t('project.projects.startDate', 'Date de début')}
                value={formData.startDate || ''}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label={t('project.projects.endDate', 'Date de fin prévue')}
                value={formData.endDate || ''}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label={t('project.projects.budget', 'Budget total (DZD)')}
                value={formData.budget ?? 0}
                onChange={(e) => setFormData({ ...formData, budget: parseFloat(e.target.value) || 0 })}
                InputProps={{
                  startAdornment: <MoneyIcon sx={{ color: 'text.secondary', mr: 1 }} fontSize="small" />
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label={t('project.projects.spent', 'Dépenses réelles (DZD)')}
                value={formData.spent ?? 0}
                onChange={(e) => setFormData({ ...formData, spent: parseFloat(e.target.value) || 0 })}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label={t('project.projects.progress', 'Progression (%)')}
                value={formData.progress ?? 0}
                onChange={(e) => setFormData({ ...formData, progress: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) })}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('project.projects.description', 'Description / Objectifs du projet')}
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </Paper>
      </GenericPage>
    );
  }

  // Columns for GenericTable
  const columns = [
    {
      id: 'code',
      label: t('project.projects.code', 'Code'),
      minWidth: 100,
      format: (_: any, row: PrjProject) => (
        <Typography variant="body2" fontWeight={600} color="primary">
          {row.code || `PRJ-${row.id}`}
        </Typography>
      )
    },
    {
      id: 'title',
      label: t('project.projects.title', 'Projet'),
      minWidth: 220,
      format: (_: any, row: PrjProject) => (
        <Box>
          <Typography variant="body2" fontWeight={700}>
            {row.title}
          </Typography>
          {row.client && (
            <Typography variant="caption" color="text.secondary">
              {row.client}
            </Typography>
          )}
        </Box>
      )
    },
    {
      id: 'status',
      label: t('project.projects.status', 'Statut'),
      minWidth: 130,
      format: (_: any, row: PrjProject) => (
        <Chip
          label={getStatusLabel(row.status, t)}
          color={getStatusColor(row.status)}
          size="small"
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
      )
    },
    {
      id: 'priority',
      label: t('project.projects.priority', 'Priorité'),
      minWidth: 100,
      format: (_: any, row: PrjProject) => (
        <Chip
          label={row.priority}
          color={getPriorityColor(row.priority)}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      id: 'progress',
      label: t('project.projects.progress', 'Progression'),
      minWidth: 140,
      format: (_: any, row: PrjProject) => (
        <Box sx={{ width: '100%' }}>
          <Stack direction="row" justifyContent="space-between" mb={0.5}>
            <Typography variant="caption" fontWeight={600}>{row.progress || 0}%</Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={row.progress || 0}
            color={row.progress >= 100 ? 'success' : 'primary'}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
      )
    },
    {
      id: 'budget',
      label: t('project.projects.budget', 'Budget / Dépenses'),
      minWidth: 160,
      format: (_: any, row: PrjProject) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {(row.budget || 0).toLocaleString()} DZD
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('project.projects.spent', 'Dépensé')}: {(row.spent || 0).toLocaleString()} DZD
          </Typography>
        </Box>
      )
    },
    {
      id: 'manager',
      label: t('project.projects.manager', 'Responsable'),
      minWidth: 130,
      format: (_: any, row: PrjProject) => (
        <Typography variant="body2">
          {row.manager || '-'}
        </Typography>
      )
    }
  ];

  return (
    <GenericPage
      title={t('project.projects.pageTitle', 'Portefeuille de Projets')}
      icon={<ProjectIcon />}
      footerEnabled={false}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      onNew={openNew}
      newButtonLabel={t('project.projects.new', 'Nouveau Projet')}
    >
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {viewMode === 'grid' ? (
        <Grid container spacing={2.5}>
          {projectsList.map((project) => (
            <Grid item xs={12} sm={6} md={4} key={project.id || project.PRJ_IDF}>
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 6px 18px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {project.code || `PRJ-${project.id}`}
                      </Typography>
                      <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.25, mt: 0.25 }}>
                        {project.title}
                      </Typography>
                    </Box>
                    <Chip
                      label={getStatusLabel(project.status, t)}
                      color={getStatusColor(project.status)}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Stack>

                  {project.client && (
                    <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                      <BusinessIcon fontSize="small" sx={{ color: 'action.active' }} />
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        {project.client}
                      </Typography>
                    </Stack>
                  )}

                  <Box sx={{ my: 2 }}>
                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                      <Typography variant="caption" color="text.secondary">{t('project.projects.progress', 'Progression')}</Typography>
                      <Typography variant="caption" fontWeight={700}>{project.progress || 0}%</Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={project.progress || 0}
                      color={project.progress >= 100 ? 'success' : 'primary'}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>

                  <Stack spacing={0.8} sx={{ color: 'text.secondary', fontSize: '0.85rem', mb: 2 }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption">{t('project.projects.budget', 'Budget')}:</Typography>
                      <Typography variant="caption" fontWeight={700}>{(project.budget || 0).toLocaleString()} DZD</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption">{t('project.projects.spent', 'Dépensé')}:</Typography>
                      <Typography variant="caption" color="warning.main" fontWeight={700}>{(project.spent || 0).toLocaleString()} DZD</Typography>
                    </Stack>
                    {project.manager && (
                      <Stack direction="row" spacing={1} alignItems="center" pt={0.5}>
                        <PersonIcon fontSize="small" sx={{ color: 'action.active' }} />
                        <Typography variant="caption">{project.manager}</Typography>
                      </Stack>
                    )}
                    {project.endDate && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <DateRangeIcon fontSize="small" sx={{ color: 'action.active' }} />
                        <Typography variant="caption">{project.startDate || ''} → {project.endDate}</Typography>
                      </Stack>
                    )}
                  </Stack>

                  <Divider sx={{ my: 1.5 }} />

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Chip
                      icon={<FlagIcon fontSize="small" />}
                      label={project.priority}
                      color={getPriorityColor(project.priority)}
                      size="small"
                      variant="outlined"
                    />

                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title={t('common.edit', 'Modifier')}>
                        <IconButton size="small" color="primary" onClick={() => openEdit(project)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete', 'Supprimer')}>
                        <IconButton size="small" color="error" onClick={() => handleDelete(project)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}

          {projectsList.length === 0 && !loading && (
            <Grid item xs={12}>
              <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                <Typography color="text.secondary">
                  {t('project.projects.empty', 'Aucun projet trouvé. Cliquez sur "Nouveau Projet" pour commencer.')}
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      ) : (
        <GenericTable
          columns={columns}
          data={projectsList}
          filterColumns={['title', 'client', 'code', 'status', 'manager']}
          searchPlaceholder={t('project.projects.searchPlaceholder', 'Rechercher un projet par titre, client, responsable...')}
          maxHeight="calc(100vh - 280px)"
          onEdit={openEdit}
          onDelete={handleDelete}
        />
      )}
    </GenericPage>
  );
};

export default ProjectsPage;
